import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tag, Calendar, ArrowRight, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Promotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPromotions();
    fetchProducts();
  }, []);

  const fetchPromotions = async () => {
    try {
      const response = await axios.get(`${API_URL}/promotions`);
      setPromotions(response.data);
    } catch (error) {
      console.error('Failed to fetch promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/products`);
      const productsMap = {};
      response.data.forEach(p => {
        productsMap[p.id] = p;
      });
      setProducts(productsMap);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const maxDiscount = (promo) => Math.round(Math.max(promo.discount_percentage || 0, ...(promo.items || []).map((i) => i.discount_percentage || 0), 0));

  const isExpired = (promo) => {
    if (!promo.end_date) return false;
    const today = new Date().toISOString().slice(0, 10);
    return promo.end_date.slice(0, 10) < today;
  };

  if (loading) {
    return (
      <div className="py-20 text-center">
        <p className="text-muted-foreground">Carregando promoções...</p>
      </div>
    );
  }

  return (
    <div data-testid="promotions-page" className="py-20 md:py-32">
      <div className="px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight mb-4">
            Promoções
          </h1>
          <p className="text-base font-light leading-relaxed text-muted-foreground">
            Ofertas especiais e descontos imperdíveis
          </p>
        </div>

        {/* Admin Button - Only visible for logged in admins */}
        {isAuthenticated && user?.is_admin && (
          <div className="flex justify-end mb-8">
            <button
              onClick={() => navigate('/admin/promocoes')}
              data-testid="add-promotion-btn"
              className="flex items-center gap-2 bg-primary text-white px-6 py-3 uppercase tracking-widest text-xs hover:bg-black transition-all"
            >
              <Plus className="w-4 h-4" />
              Nova Promoção
            </button>
          </div>
        )}

        {promotions.length === 0 ? (
          <div className="text-center py-20">
            <Tag className="w-16 h-16 mx-auto text-muted-foreground mb-6" strokeWidth={1} />
            <h2 className="font-heading text-2xl font-light mb-4">Nenhuma promoção ativa no momento</h2>
            <p className="text-muted-foreground mb-8">
              Fique de olho! Em breve teremos novidades para você.
            </p>
            <Link
              to="/catalogo"
              className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 uppercase tracking-widest text-xs hover:bg-black transition-all duration-500"
            >
              Ver Catálogo
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="space-y-20">
            {promotions.map((promotion) => (
              <div key={promotion.id} className="border border-border">
                {/* Banner da Promoção */}
                {promotion.banner_image && (
                  <div className="relative h-64 md:h-80 overflow-hidden">
                    <img
                      src={promotion.banner_image}
                      alt={promotion.title}
                      className="w-full h-full object-cover"
                    />
                    {maxDiscount(promotion) > 0 && (
                      <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2">
                        <span className="font-accent text-lg font-bold">
                          até -{maxDiscount(promotion)}%
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Info da Promoção */}
                <div className="p-8">
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <h2 className="font-heading text-3xl md:text-4xl font-light">
                      {promotion.title}
                    </h2>
                    {maxDiscount(promotion) > 0 && !promotion.banner_image && (
                      <span data-testid={`promotion-max-discount-${promotion.id}`} className="bg-red-600 text-white px-3 py-1 text-sm font-medium">
                        até -{maxDiscount(promotion)}% OFF
                      </span>
                    )}
                  </div>

                  <p className="text-muted-foreground mb-6">{promotion.description}</p>

                  {(promotion.start_date || promotion.end_date) && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {promotion.start_date && `De ${formatDate(promotion.start_date)}`}
                        {promotion.start_date && promotion.end_date && ' '}
                        {promotion.end_date && `até ${formatDate(promotion.end_date)}`}
                      </span>
                    </div>
                  )}

                  {/* Produtos da Promoção */}
                  {(() => {
                    const ids = promotion.items?.length ? promotion.items.map((i) => i.product_id) : (promotion.product_ids || []);
                    const promoProducts = ids.map((id) => products[id]).filter(Boolean);
                    if (promoProducts.length === 0) return null;
                    return (
                    <div>
                      <h3 className="font-accent text-xs uppercase tracking-widest mb-6">
                        Produtos em Promoção
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {promoProducts.map((product) => (
                            <Link
                              key={product.id}
                              to={`/produto/${product.id}`}
                              data-testid={`promotion-product-${product.id}`}
                              className="group"
                            >
                              <div className="relative aspect-square overflow-hidden bg-neutral-50 mb-4">
                                {product.promotional_price && (
                                  <span data-testid={`promotion-product-discount-${product.id}`} className="absolute top-3 left-3 z-10 bg-red-600 text-white px-2 py-1 text-xs font-semibold">
                                    -{Math.round(product.discount_percentage)}%
                                  </span>
                                )}
                                <img
                                  src={product.images?.[0] || '/placeholder.svg'}
                                  alt={product.name}
                                  className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-700"
                                />
                              </div>
                              <h4 className="text-sm font-medium mb-2 group-hover:text-primary transition-colors">
                                {product.name}
                              </h4>
                              <div className="flex items-baseline gap-2 flex-wrap">
                                {product.promotional_price && (
                                  <span className="text-xs text-muted-foreground line-through">
                                    R$ {(product.original_price || product.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                  </span>
                                )}
                                <span className={`text-base font-medium ${product.promotional_price ? 'text-red-600' : ''}`}>
                                  R$ {(product.promotional_price || product.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </span>
                              </div>
                            </Link>
                        ))}
                      </div>
                    </div>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Promotions;
